/**
 * Illustrated town scene (temple gopuram, hills, river, bridge, palms, birds) used as the
 * hero background until admins upload real photographs. Pure SVG: no external assets.
 */
export function HeroScene({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 1440 640" preserveAspectRatio="xMidYMid slice" className={className} aria-hidden>
      <defs>
        <linearGradient id="hs-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1c3fc4" />
          <stop offset=".45" stopColor="#4f7ff2" />
          <stop offset=".8" stopColor="#a9c6ff" />
          <stop offset="1" stopColor="#ffe1b8" />
        </linearGradient>
        <linearGradient id="hs-river" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#6f9dff" />
          <stop offset="1" stopColor="#2450e0" />
        </linearGradient>
        <linearGradient id="hs-gopuram" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#c9762a" />
          <stop offset=".5" stopColor="#f2b066" />
          <stop offset="1" stopColor="#b8621d" />
        </linearGradient>
        <radialGradient id="hs-sun" cx=".5" cy=".5" r=".5"><stop offset="0" stopColor="#fff6dc" /><stop offset="1" stopColor="#fff6dc" stopOpacity="0" /></radialGradient>
      </defs>
      <rect width="1440" height="640" fill="url(#hs-sky)" />
      <circle cx="1120" cy="300" r="180" fill="url(#hs-sun)" />
      <circle cx="1120" cy="300" r="46" fill="#fff4d6" />
      {/* clouds */}
      <g fill="#fff" opacity=".55">
        <ellipse cx="300" cy="120" rx="110" ry="22" /><ellipse cx="360" cy="105" rx="70" ry="24" />
        <ellipse cx="960" cy="90" rx="120" ry="20" /><ellipse cx="1020" cy="78" rx="70" ry="22" />
      </g>
      {/* far hills */}
      <path d="M0 400 Q160 300 320 360 T640 330 T960 350 T1260 300 T1440 340 V640 H0Z" fill="#6c8fd8" opacity=".55" />
      <path d="M0 430 Q200 350 420 410 T820 380 T1180 400 T1440 380 V640 H0Z" fill="#3f8a6a" />
      <path d="M0 470 Q240 420 480 455 T960 440 T1440 450 V640 H0Z" fill="#2f7a58" />
      {/* river */}
      <path d="M0 520 Q360 490 720 515 T1440 505 V640 H0Z" fill="url(#hs-river)" />
      <g stroke="#fff" strokeOpacity=".45" strokeWidth="2" fill="none">
        <path d="M120 560 q40 -6 80 0" /><path d="M520 590 q50 -8 100 0" /><path d="M980 570 q40 -6 80 0" /><path d="M1240 600 q50 -8 100 0" />
      </g>
      {/* bridge */}
      <g stroke="#e9eefc" strokeWidth="4" fill="none">
        <path d="M1000 505 Q1200 400 1400 505" />
        <path d="M1000 505 H1400" strokeWidth="7" />
        {Array.from({ length: 9 }, (_, i) => 1020 + i * 45).map((x) => <path key={x} d={`M${x} 505 V${505 - Math.sin(((x - 1000) / 400) * Math.PI) * 100 + 6}`} strokeWidth="2" />)}
      </g>
      <g fill="#d5ddf5"><rect x="996" y="505" width="10" height="34" /><rect x="1394" y="505" width="10" height="34" /><rect x="1195" y="505" width="10" height="30" /></g>
      {/* palms */}
      {[[70, 470, 1], [640, 480, 0.8], [1060, 470, 0.9]].map(([x, y, s]) => (
        <g key={x} transform={`translate(${x} ${y}) scale(${s})`}>
          <path d="M0 0 Q6 -60 2 -120" stroke="#5b3b1d" strokeWidth="8" fill="none" />
          <g fill="#1f6b46"><path d="M2 -120 q-50 -10 -80 20 q40 -30 80 -20z" /><path d="M2 -120 q50 -10 80 20 q-40 -30 -80 -20z" /><path d="M2 -120 q-20 -40 -60 -40 q40 10 60 40z" /><path d="M2 -120 q20 -40 60 -40 q-40 10 -60 40z" /></g>
        </g>
      ))}
      {/* gopuram */}
      <g transform="translate(720 150) scale(.92)">
        <rect x="-10" y="330" width="300" height="40" fill="#8a4b17" />
        {[0, 1, 2, 3, 4, 5, 6].map((i) => {
          const w = 280 - i * 34;
          const h = 46;
          return (
            <g key={i}>
              <rect x={(280 - w) / 2} y={330 - (i + 1) * h} width={w} height={h} fill="url(#hs-gopuram)" stroke="#8a4b17" strokeWidth="2" />
              {Array.from({ length: Math.max(1, Math.floor(w / 26)) }, (_, k) => <rect key={k} x={(280 - w) / 2 + 8 + k * 26} y={330 - (i + 1) * h + 12} width="10" height="22" rx="4" fill="#8a4b17" opacity=".55" />)}
            </g>
          );
        })}
        <path d="M100 8 Q140 -30 180 8 Z" fill="#c9762a" stroke="#8a4b17" strokeWidth="2" />
        <g fill="#ffd36b">{[110, 125, 140, 155, 170].map((x) => <circle key={x} cx={x} cy={-8} r="5" />)}</g>
        <rect x="112" y="282" width="56" height="88" rx="28" fill="#5c2f0c" />
      </g>
      {/* birds */}
      <g stroke="#ffffff" strokeWidth="3" fill="none" strokeLinecap="round" opacity=".9">
        <path d="M1180 150 q12 -12 24 0 q12 -12 24 0" /><path d="M1250 120 q9 -9 18 0 q9 -9 18 0" /><path d="M1120 110 q7 -7 14 0 q7 -7 14 0" />
      </g>
    </svg>
  );
}
